"use client"

import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CalendarClock } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useStore } from '@/lib/store'
import { numDateText, useT } from '@/lib/i18n'
import { businessDay } from '@/lib/business-day'
import { ownerWindow, shouldShowWindow, windowSeenKey } from '@/lib/expiry'
import { fmtQty } from '@/lib/stocktake'

/**
 * 1.61 — the owner hears about expiring batches without opening the stock doctor
 * („ხშირად მფლობელები არ შედიან საწყობის მენეჯერში და ესაა პრობლემა", 2026-10-24):
 * on the owner's first screen of the day, if any batch has expired or expires
 * within 7 days. Once a calendar day per user per device (localStorage, a
 * convenience); never to a cashier, in a support session, a locked screen, the
 * kiosk or the handheld terminal. The day is marked only when the window shows.
 */
/** „2 პარტიას ვადა გაუვიდა · 3 პარტიას 7 დღეში გასდის · ₾94" — a zero part is left out */
export function windowLine(w: { expired: number; soon: number; value: number }, t: ReturnType<typeof useT>): string {
  return [
    w.expired > 0 ? t('acc.lot.wExpired', { n: w.expired }) : null,
    w.soon > 0 ? t('acc.lot.wSoon', { n: w.soon }) : null,
    `₾${w.value.toFixed(2)}`,
  ].filter(Boolean).join(' · ')
}

export function ExpiryWindow() {
  const t = useT()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isOwner = useStore((s) => s.userRole === 'owner')
  const support = useStore((s) => s.supportSession !== null)
  const locked = useStore((s) => s.isLocked)
  const kiosk = useStore((s) => s.kioskMode)
  const userId = useStore((s) => s.cachedUserId)
  const orgId = useStore((s) => s.currentOrg?.id ?? null)
  const loaded = useStore((s) => s.stockLotsLoaded)
  const lots = useStore((s) => s.stockLots)
  const products = useStore((s) => s.products)
  const branches = useStore((s) => s.branches)
  const [open, setOpen] = useState(false)

  const w = useMemo(() => (loaded ? ownerWindow(lots, products) : null), [loaded, lots, products])

  useEffect(() => {
    if (open || !isOwner || support || locked || kiosk || !userId || !orgId || pathname.startsWith('/terminal')) return
    const key = windowSeenKey(userId, orgId)
    const today = businessDay(Date.now())
    let last: string | null = null
    try { last = localStorage.getItem(key) } catch { /* private window: show it, once per load */ }
    if (!shouldShowWindow(last, today, loaded, !!w)) return
    try { localStorage.setItem(key, today) } catch { /* ignore */ }
    setOpen(true)
  }, [open, isOwner, support, locked, kiosk, userId, orgId, pathname, loaded, w])

  if (!open || !w) return null
  const branchName = (id: string) => branches.find((b) => b.id === id)?.name ?? ''

  return (
    <Dialog open onOpenChange={(o) => { if (!o) setOpen(false) }}>
      <DialogContent className="sm:max-w-md gap-4 p-5">
        <div className="flex items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <CalendarClock className="size-5" />
          </span>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-bold">{t('acc.lot.window')}</DialogTitle>
            <DialogDescription className="text-[13px] text-muted-foreground">
              {windowLine(w, t)}
            </DialogDescription>
          </div>
        </div>
        <div className="flex flex-col divide-y divide-border rounded-xl border border-border">
          {w.rows.map((r) => (
            <div key={r.lot.id} className="flex items-center gap-3 px-3 py-2 text-[13px]">
              <span className="flex-1 min-w-0 truncate font-semibold">
                {r.name}{branches.length > 1 && <span className="font-normal text-muted-foreground"> · {branchName(r.lot.branch_id)}</span>}
              </span>
              <span className={(r.days ?? 0) < 0 ? 'font-bold text-red-600 dark:text-red-400 tabular-nums' : 'font-bold text-amber-700 dark:text-amber-400 tabular-nums'}>
                {numDateText(r.lot.expiry_date!)}
              </span>
              <span className="w-12 text-right tabular-nums text-muted-foreground">{fmtQty(r.lot.qty)}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">{t('acc.lot.windowLater')}</Button>
          <Button onClick={() => { setOpen(false); navigate('/accounting?tab=doctor&doctor=expiry') }} className="rounded-xl font-bold">
            {t('acc.lot.windowSee')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
